export type WaiverFieldType =
  | 'name'
  | 'email'
  | 'phone'
  | 'textBody'
  /**
   * @deprecated Retired. This asked the signer to declare that they were a
   * minor; participants now state a date of birth and Fleetmaid derives the
   * classification, so the declaration is redundant and the widget is no
   * longer offered in the builder or rendered on any sign form.
   *
   * KEPT IN THE UNION ON PURPOSE. Waiver docs saved before the change still
   * carry fields of this type, and signed records still carry their captured
   * value. Dropping the member would make those docs fail to type-check on
   * read and would break the signed-waiver viewer, which must keep rendering
   * historical answers. Nothing creates new ones.
   */
  | 'isUnder18'
  | 'signature'
  | 'guardianName'
  | 'guardianSignature';

export interface WaiverField {
  id: string;
  type: WaiverFieldType;
  label: string;
  required: boolean;
  order: number;
  placeholder?: string;
  value?: any;
}

export interface WaiverFormConfig {
  id?: string;
  /**
   * Owning company id. Persisted on every builder waiver doc; hydrated by
   * {@link WaiverBuilderService.hydrateWaiver}. Consumers on the public
   * sign surface use this to trigger a company-scoped threshold load
   * (no partner-side cache in that flow).
   */
  companyId?: string;
  name: string;
  version: number;
  versionLabel: string;
  fields: WaiverField[];
  createdAt?: Date;
  updatedAt?: Date;
  /**
   * @deprecated Superseded by per-group `minorWaiverId` on
   * {@link BuilderWaiverGroup}. Old docs may still carry this flag from
   * before the group model was introduced; readers ignore it in favor
   * of the group's `minorWaiverId`, and {@link WaiverBuilderService.saveWaiver}
   * no longer writes it. Kept on the interface for back-compat during
   * the auto-wrap transition — safe to remove once no live docs carry it.
   */
  isMinorWaiver?: boolean;
  /**
   * Back-reference to the explicit {@link BuilderWaiverGroup} this waiver
   * belongs to. Absent for waivers created before the group model
   * existed; those waivers are auto-wrapped as single-waiver implicit
   * groups at read time (see {@link WaiverBuilderService.loadCompanyWaiverGroups}).
   */
  groupId?: string;
  /**
   * @deprecated Moved to a company-wide setting stored at
   * `companies/{companyId}.waiverMinorAgeThreshold` (read via
   * {@link WaiverBuilderService.getMinorAgeThreshold}). Old docs still
   * carry this field — {@link WaiverBuilderService.loadMinorAgeThreshold}
   * uses the mode across per-waiver values to seed the global setting
   * on first load. New waivers do not write to this field.
   */
  minorAgeThreshold?: number;
  /**
   * Firebase Storage download URL for an optional PDF attached to this
   * waiver. Ports the old waiver-editor's PDF-upload behavior — the
   * partner uploads a template PDF (the actual legal text) and it's
   * embedded as an `<iframe>` above the form fields on every sign
   * surface (preview, public sign page, in-person modal, post-payment
   * wizard). The form fields still capture the signature and any
   * ancillary inputs; the PDF replaces or supplements the `textBody`
   * content. Empty/undefined = no PDF (form-only waiver).
   */
  pdfContent?: string;
  /**
   * Timestamp of the most recent explicit "Publish new version" click.
   * `updatedAt` moves on any Save (draft or publish); `publishedAt`
   * moves only on Publish. Absent on waivers that have been saved but
   * never published (first save auto-publishes v1, so most rows will
   * have this set).
   */
  publishedAt?: Date;
}

/**
 * Lightweight row for the version-history dropdown. Full config bodies
 * for old versions live in the `builderWaivers/{id}/versions` subcollection
 * and are fetched lazily via {@link WaiverBuilderService.getWaiverVersion}
 * when the partner clicks into a specific one.
 */
export interface WaiverVersionSummary {
  version: number;
  versionLabel: string;
  name: string;
  publishedAt?: Date;
}

/**
 * A named collection of interchangeable waiver variants (form + PDF
 * templates). Products attach to a group, not a specific waiver — at
 * sign time the group resolves to ONE of its `waiverIds` based on the
 * participant (adult vs minor). The old "designated Minor Waiver"
 * company-singleton flag is superseded by per-group `minorWaiverId`.
 *
 * Persisted at `builderWaiverGroups/{groupId}`. Waivers that were
 * created before the group model existed have no `groupId` and are
 * auto-wrapped as single-waiver implicit groups at load time — those
 * do NOT have a `builderWaiverGroups/*` doc; the resolver treats the
 * waiver id itself as the group id (see
 * {@link WaiverBuilderService.resolveWaiverForParticipant}).
 */
export interface BuilderWaiverGroup {
  id: string;
  companyId: string;
  name: string;
  /**
   * Ordered list of waiver ids that belong to this group. First entry
   * is not implicitly the active one — that's chosen by
   * `activeWaiverId`. Length ≥ 1 by invariant (a group with zero
   * waivers is deleted).
   */
  waiverIds: string[];
  /** The variant shown to adult participants and used as the "default"
   *  variant if `minorWaiverId` is unset. Must be present in `waiverIds`. */
  activeWaiverId: string;
  /** Optional variant swapped in for minor participants. When unset and
   *  the participant is a minor, resolution falls back to
   *  `activeWaiverId` (which may itself carry guardian fields). */
  minorWaiverId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/** Default value used everywhere `minorAgeThreshold` is read. Kept as a
 *  named export so tests and the "Add Participant" form label share one
 *  source of truth for the fallback. */
export const DEFAULT_MINOR_AGE_THRESHOLD = 18;

/**
 * Upper bound on how many variants a single BuilderWaiverGroup may hold.
 * Shared between the service (throws at write time) and the waiver-list
 * UI (disables the "Add variant" trigger at the cap) so both agree on
 * the same number. Chosen to comfortably cover common shapes — default +
 * minor + language variants + a spare — without letting a group grow
 * unbounded and turn the sidebar into an infinite scroll.
 */
export const MAX_VARIANTS_PER_GROUP = 5;

export interface ProductWaiverAssignment {
  productId: string;
  productNumber: string;
  productName: string;
  productCategory: string;
  selected: boolean;
}

/**
 * A row in the waiver-builder's attach modal after the switch to
 * group-level attachment. Persistence lives on
 * `productGroups/{groupId}.waivers[]` — products no longer carry the
 * waiver array. `productCount` is displayed alongside the group name
 * so the partner can sanity-check the scope of the toggle before saving.
 */
export interface ProductGroupWaiverAssignment {
  groupId: string;
  groupName: string;
  categoryName: string;
  productCount: number;
  selected: boolean;
}

export interface ReplacementProductAssignment {
  productId: string;
  productNumber: string;
  productName: string;
  productCategory: string;
  replacementWaiverId: string;
}

export interface RequiredWaiverSummary {
  id: string;
  name: string;
  isLegacy: boolean;
  builderConfig?: WaiverFormConfig;
  legacyDoc?: any;
}

export interface WaiverCustomer {
  name: string;
  email: string;
  phone: string;
}

export interface WaiverFieldTemplate {
  type: WaiverFieldType;
  label: string;
  placeholder?: string;
  icon: string;
}

export const WAIVER_FIELD_TEMPLATES: WaiverFieldTemplate[] = [
  { type: 'textBody', label: 'Text Body', placeholder: 'Enter waiver text content', icon: 'fa-align-left' },
  { type: 'name', label: 'Name', placeholder: 'Enter full name', icon: 'fa-user' },
  { type: 'email', label: 'Email', placeholder: 'Enter email address', icon: 'fa-envelope' },
  { type: 'phone', label: 'Phone Number', placeholder: 'Enter phone number', icon: 'fa-phone' },
  // Replaced the "Is Under 18" widget. That widget asked the SIGNER to
  // declare whether they were a minor; participants now state a date of
  // birth and Fleetmaid classifies them, so the declaration is redundant.
  // Guardian capture itself is not — it was only ever reachable as the
  // widget's auto-paired companion, so the pair is anchored here instead
  // and remains addable on its own.
  { type: 'guardianSignature', label: 'Parent/Guardian Signature', icon: 'fa-user-shield' },
  { type: 'signature', label: 'Signature', icon: 'fa-pen-fancy' },
];

/**
 * `guardianName` is auto-inserted by the builder alongside
 * `guardianSignature` (it is not separately draggable), so it does not
 * appear in WAIVER_FIELD_TEMPLATES. `isUnder18` is retired and no longer
 * offered, but legacy waiver docs still carry it — this map keeps icon
 * lookup total so rendering an old field never falls through to a blank.
 */
const EXTRA_FIELD_ICONS: Partial<Record<WaiverFieldType, string>> = {
  guardianName: 'fa-user-tag',
  isUnder18: 'fa-child',
};

/** Look up a Font Awesome icon class for any WaiverFieldType, including auto-inserted ones. */
export function iconForFieldType(type: WaiverFieldType): string {
  const fromTemplate = WAIVER_FIELD_TEMPLATES.find(t => t.type === type);
  if (fromTemplate) return fromTemplate.icon;
  return EXTRA_FIELD_ICONS[type] ?? 'fa-plus';
}
